import { isPlatformBrowser } from "@angular/common";
import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

@Injectable({
  providedIn: "root"
})
export class ForumHtmlSanitizerService {
  private readonly ALLOWED_TAGS = [
    "b",
    "i",
    "strong",
    "em",
    "strike",
    "a",
    "img",
    "br",
    "span",
    "div",
    "p",
    "ul",
    "li",
    "ol",
    "pre",
    "code",
    "blockquote",
    // KaTeX tags
    "math",
    "semantics",
    "mrow",
    "mi",
    "mo",
    "mn",
    "mtext",
    "mspace",
    "msup",
    "msub",
    "mfrac",
    "mroot",
    "msqrt",
    "mover",
    "munder",
    "munderover",
    "mtable",
    "mtr",
    "mtd",
    "annotation"
  ];
  private readonly ALLOWED_ATTRIBUTES = [
    "href",
    "target",
    "src",
    "class",
    "alt",
    "width",
    "height",
    "rel",
    "style",
    "contenteditable",
    "data-mention",
    "data-username",
    "data-display-name"
  ];
  private readonly ALLOWED_STYLES = [
    "font-size",
    // KaTeX styles
    "margin-left",
    "margin-right",
    "margin-top",
    "margin-bottom",
    "vertical-align",
    "text-align",
    "display"
  ];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly domSanitizer: DomSanitizer
  ) {}

  sanitizeHtml(html: string): SafeHtml {
    // In SSR environment, just use Angular's default sanitizer
    if (!isPlatformBrowser(this.platformId)) {
      return this.domSanitizer.sanitize(1, html) || "";
    }

    // Apply our custom sanitization directly (skip Angular's sanitizer which strips styles)
    const customSanitized = this._customSanitize(html);

    // Bypass security for our custom sanitized content
    return this.domSanitizer.bypassSecurityTrustHtml(customSanitized);
  }

  private _customSanitize(html: string): string {
    // This should not be called in SSR, but add a guard just in case
    if (!isPlatformBrowser(this.platformId)) {
      return html;
    }

    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Process all elements
    this._processElement(tempDiv);

    return tempDiv.innerHTML;
  }

  private _processElement(element: Element): void {
    // Get all child elements (we need to convert to array to avoid issues when removing elements)
    const children = Array.from(element.children);

    for (const child of children) {
      const tagName = child.tagName.toLowerCase();

      // Check if tag is allowed
      if (!this.ALLOWED_TAGS.includes(tagName)) {
        // Replace disallowed tag with its content
        const parent = child.parentNode;
        if (parent) {
          while (child.firstChild) {
            parent.insertBefore(child.firstChild, child);
          }
          parent.removeChild(child);
        }
        continue;
      }

      // Process attributes
      const attributes = Array.from(child.attributes);
      for (const attr of attributes) {
        // Allow KaTeX class if it starts with 'katex'
        if (attr.name === "class" && attr.value.split(" ").some(cls => cls.startsWith("katex"))) {
          // Keep the class attribute
          continue;
        } else if (!this.ALLOWED_ATTRIBUTES.includes(attr.name)) {
          child.removeAttribute(attr.name);
        } else if (attr.name === "style") {
          // Process style attribute
          const sanitizedStyle = this._sanitizeStyles(attr.value);
          if (sanitizedStyle) {
            child.setAttribute("style", sanitizedStyle);
          } else {
            child.removeAttribute("style");
          }
        } else if (attr.name === "href" || attr.name === "src") {
          // Ensure URLs are safe
          const url = attr.value;
          if (!this._isUrlSafe(url)) {
            child.removeAttribute(attr.name);
          }
        }
      }

      // Recursively process child elements
      this._processElement(child);
    }
  }

  private _sanitizeStyles(styleString: string): string {
    const styles = styleString.split(";").filter(s => s.trim());
    const sanitizedStyles: string[] = [];

    for (const style of styles) {
      const [property, value] = style.split(":").map(s => s.trim());

      if (property && value && this.ALLOWED_STYLES.includes(property)) {
        // Additional validation for specific properties
        if (property === "font-size" && this._isValidFontSize(value)) {
          sanitizedStyles.push(`${property}: ${value}`);
        } else if (property === "display" && this._isValidDisplay(value)) {
          sanitizedStyles.push(`${property}: ${value}`);
        } else if (
          ["margin-left", "margin-right", "margin-top", "margin-bottom"].includes(property) &&
          this._isValidSpacing(value)
        ) {
          sanitizedStyles.push(`${property}: ${value}`);
        } else if (property === "vertical-align" && this._isValidVerticalAlign(value)) {
          sanitizedStyles.push(`${property}: ${value}`);
        } else if (property === "text-align" && this._isValidTextAlign(value)) {
          sanitizedStyles.push(`${property}: ${value}`);
        }
      }
    }

    return sanitizedStyles.join("; ");
  }

  private _isValidFontSize(value: string): boolean {
    // Allow percentage values, em, rem, px
    const fontSizePattern = /^(\d+(\.\d+)?(px|em|rem|%))$/;
    return fontSizePattern.test(value.trim());
  }

  private _isValidDisplay(value: string): boolean {
    const allowedValues = ["inline", "block", "inline-block", "none"];
    return allowedValues.includes(value.trim());
  }

  private _isValidSpacing(value: string): boolean {
    // Allow px, em, rem values (positive or negative) and auto
    const spacingPattern = /^(-?\d+(\.\d+)?(px|em|rem)|auto|0)$/;
    return spacingPattern.test(value.trim());
  }

  private _isValidVerticalAlign(value: string): boolean {
    const allowedValues = ["baseline", "sub", "super", "text-top", "text-bottom", "middle", "top", "bottom"];
    // Also allow percentage and length values
    const verticalAlignPattern = /^(-?\d+(\.\d+)?(px|em|rem|%))$/;
    return allowedValues.includes(value.trim()) || verticalAlignPattern.test(value.trim());
  }

  private _isValidTextAlign(value: string): boolean {
    const allowedValues = ["left", "right", "center", "justify"];
    return allowedValues.includes(value.trim());
  }

  private _isUrlSafe(url: string): boolean {
    // Allow http, https, mailto, and relative URLs
    const allowedProtocols = ["http:", "https:", "mailto:"];

    // Check for data URLs (base64 images)
    if (url.toLowerCase().startsWith("data:")) {
      // Only allow data URLs for images
      return url.toLowerCase().startsWith("data:image/");
    }

    try {
      // Try to parse as absolute URL
      const parsedUrl = new URL(url);
      return allowedProtocols.includes(parsedUrl.protocol);
    } catch {
      // If it fails, it might be a relative URL, which is allowed
      // But check for javascript: protocol
      return !url.toLowerCase().startsWith("javascript:");
    }
  }
}
